import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Pagination,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

interface Product {
  productId: number;
  name: string;
  imageUrl: string;
  categoryId: number;
  subCategoryId: number;
  isActive: boolean;
  description: string;
}

interface Category {
  categoryId: number;
  name: string;
}

interface SubCategory {
  subCategoryId: number;
  name: string;
  categoryId: number;
}

const ProductCard = React.memo(
  ({
    product,
    onEdit,
    onDelete,
  }: {
    product: Product;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
  }) => {
    const [imgLoaded, setImgLoaded] = React.useState(false);
    const [imgError, setImgError] = React.useState(false);

    const src = product.imageUrl ? `${IMAGE_URL}${product.imageUrl}` : '';

    React.useEffect(() => {
      setImgLoaded(!src);
      setImgError(false);
      if (!src) return;
      let mounted = true;
      const img = new Image();
      let timer: any = null;
      img.src = src;
      const handleLoad = () => mounted && setImgLoaded(true);
      const handleError = () =>
        mounted && (setImgLoaded(true), setImgError(true));
      img.onload = handleLoad;
      img.onerror = handleError;
      timer = setTimeout(
        () => mounted && (setImgLoaded(true), setImgError(true)),
        8000,
      );
      return () => {
        mounted = false;
        if (timer) clearTimeout(timer);
        img.onload = null;
        img.onerror = null;
      };
    }, [src]);

    return (
      <Card sx={{ height: 280 }}>
        <Box sx={{ height: 100, position: 'relative', bgcolor: '#f5f5f5' }}>
          {!imgLoaded && (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={100}
              sx={{ position: 'absolute', inset: 0 }}
            />
          )}
          {src && (
            <Box
              component="img"
              src={src}
              alt={product.name || 'product'}
              loading="lazy"
              onLoad={() => setImgLoaded(true)}
              onError={() => {
                setImgLoaded(true);
                setImgError(true);
              }}
              sx={{
                display: imgLoaded ? 'block' : 'none',
                width: '100%',
                height: 100,
                objectFit: 'cover',
              }}
            />
          )}
          {!src && imgLoaded && (
            <Box
              sx={{
                width: '100%',
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption">No image</Typography>
            </Box>
          )}
        </Box>

        <CardContent>
          <Typography
            variant="subtitle1"
            style={{ color: product.isActive ? '#000' : '#8a8a8a' }}
          >
            {product.name}
          </Typography>
          <Typography
            style={{
              color: product.isActive ? '#535353' : '#8a8a8a',
              fontSize: 12,
            }}
          >
            {product.description?.length > 80
              ? product.description.slice(0, 80) + '...'
              : product.description}
          </Typography>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between' }}>
          <IconButton onClick={() => onEdit(product.productId)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => onDelete(product.productId)}>
            <DeleteIcon />
          </IconButton>
        </CardActions>
      </Card>
    );
  },
);

const ProductsGrid: React.FC<{
  products: Product[];
  loading: boolean;
  page: number;
  pages: number;
  onPageChange: (p: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ products, loading, page, pages, onPageChange, onEdit, onDelete }) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 12 }).map((_, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
            <Skeleton variant="rectangular" height={160} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        {products.map((p) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={p.productId}>
            <ProductCard product={p} onEdit={onEdit} onDelete={onDelete} />
          </Grid>
        ))}
      </Grid>

      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            page={page}
            count={pages}
            onChange={(_, v) => onPageChange(v)}
          />
        </Box>
      )}
    </>
  );
};

const ProductList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = React.useState<Product[]>([]);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [subCategories, setSubCategories] = React.useState<SubCategory[]>([]);
  const [categoryId, setCategoryId] = React.useState<number | null>(null);
  const [subCategoryId, setSubCategoryId] = React.useState<number | null>(null);
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState(search);
  const [sortField, setSortField] = React.useState<'name' | 'updatedAt'>(
    'name',
  );
  const [sortOrder, setSortOrder] = React.useState<'ASC' | 'DESC'>('ASC');

  const controllerRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  const loadProducts = async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    const params: any = { page, limit: 8, sortField, sortOrder };
    if (categoryId) params.categoryId = categoryId;
    if (subCategoryId) params.subCategoryId = subCategoryId;
    if (debouncedSearch) params.search = debouncedSearch;

    try {
      const { data } = await axios.get(`${API_URL}/products`, {
        params,
        signal: controller.signal,
      });
      setProducts(data.data || []);
      setPages(data.pages || 1);
    } catch (e: any) {
      if (e.name !== 'CanceledError') throw e;
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const fetchProducts = async () => {
      await loadProducts();
    };
    fetchProducts();
  }, [page, categoryId, subCategoryId, debouncedSearch, sortField, sortOrder]);

  React.useEffect(() => {
    axios.get(`${API_URL}/categories`).then((r) => setCategories(r.data || []));
    axios
      .get(`${API_URL}/sub-categories`)
      .then((r) => setSubCategories(r.data?.data || []));
  }, []);

  const handleDelete = async (id: number) => {
    if (!confirm('Видалити товар?')) return;
    await axios.delete(`${API_URL}/products/${id}`);
    loadProducts();
  };

  const visibleSubCategories = React.useMemo(() => {
    if (categoryId === null) return [];
    return subCategories.filter((s) => s.categoryId === categoryId);
  }, [subCategories, categoryId]);

  return (
    <Box sx={{ display: 'flex', p: 2 }}>
      <Box sx={{ width: 260, mr: 3 }}>
        <List>
          <ListItemButton
            selected={!categoryId && !subCategoryId}
            onClick={() => (
              setCategoryId(null),
              setSubCategoryId(null),
              setPage(1)
            )}
          >
            <ListItemText primary="Всі" />
            {!categoryId && !subCategoryId && <CheckIcon fontSize="small" />}
          </ListItemButton>
          {categories.map((c) => (
            <ListItemButton
              key={c.categoryId}
              selected={c.categoryId === categoryId}
              onClick={() => (
                setCategoryId(c.categoryId),
                setSubCategoryId(null),
                setPage(1)
              )}
            >
              <ListItemText primary={c.name} />
              {c.categoryId === categoryId && <CheckIcon fontSize="small" />}
            </ListItemButton>
          ))}
        </List>

        {visibleSubCategories.length > 0 && (
          <List>
            {visibleSubCategories.map((sc) => (
              <ListItemButton
                key={sc.subCategoryId}
                selected={sc.subCategoryId === subCategoryId}
                onClick={() => (setSubCategoryId(sc.subCategoryId), setPage(1))}
              >
                <ListItemText primary={sc.name} />
                {sc.subCategoryId === subCategoryId && (
                  <CheckIcon fontSize="small" />
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">Продукція</Typography>
          <Button variant="contained" onClick={() => navigate('/products/new')}>
            Додати продукцію
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Пошук продуктів..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Сортування</InputLabel>
            <Select
              label="Сортування"
              value={`${sortField}_${sortOrder}`}
              onChange={(e) => {
                const [field, order] = String(e.target.value).split('_');
                setSortField(field as any);
                setSortOrder(order as any);
                setPage(1);
              }}
            >
              <MenuItem value="name_ASC">Назва (A–Z)</MenuItem>
              <MenuItem value="name_DESC">Назва (Z–A)</MenuItem>
              <MenuItem value="updatedAt_DESC">Нові</MenuItem>
              <MenuItem value="updatedAt_ASC">Старі</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <ProductsGrid
          products={products}
          loading={loading}
          page={page}
          pages={pages}
          onPageChange={setPage}
          onEdit={(id) => navigate(`/products/${id}/edit`)}
          onDelete={handleDelete}
        />
      </Box>
    </Box>
  );
};

export default ProductList;
